import React, { useState, useEffect } from 'react';
import {
  ChevronDown, Save, HandCoins, Users, Calendar, IndianRupee,
  Hash, Percent, FileText, Calculator, Plus, X, Trash2
} from 'lucide-react';
import { wagesAPI, establishmentAPI, employeeAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useAuth, useActiveEstId } from './AuthContext';
import DateInput from './DateInput';
import EstablishmentCard from './EstablishmentCard';
import './UpdateEmployee.css';
import './AttendanceRegistry.css';

const LoansAdvances = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  const [loans, setLoans] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedEstablishment, setSelectedEstablishment] = useState(lockedEstId || '');

  const [formData, setFormData] = useState({
    establishment: lockedEstId || '',
    employee: '',
    loanType: 'salary-advance',
    otherLoanType: '',
    amount: '',
    issueDate: new Date().toISOString().split('T')[0],
    repaymentStartDate: '',
    installments: '',
    interestRate: '0',
    reason: '',
    status: 'active'
  });

  const loanTypes = [
    { value: 'salary-advance', label: 'Salary Advance' },
    { value: 'personal-loan', label: 'Personal Loan' },
    { value: 'emergency-loan', label: 'Emergency Loan' },
    { value: 'home-loan', label: 'Home Loan' },
    { value: 'education-loan', label: 'Education Loan' },
    { value: 'vehicle-loan', label: 'Vehicle Loan' },
    { value: 'travel-advance', label: 'Travel Advance' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (lockedEstId) {
      setSelectedEstablishment(lockedEstId);
      setFormData(prev => ({ ...prev, establishment: lockedEstId }));
      fetchEmployees(lockedEstId);
    }
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lockedEstId) {
      fetchEmployees(lockedEstId);
      setLoading(false);
    } else {
      fetchEstablishments();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedEstablishment) {
      fetchLoans();
    }
  }, [selectedEstablishment]);

  const fetchEstablishments = async () => {
    try {
      const response = await establishmentAPI.getAll();
      const estabs = response.establishments || [];
      setEstablishments(estabs);
      if (estabs.length > 0) {
        setSelectedEstablishment(estabs[0].id.toString());
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setLoading(false);
    }
  };

  const fetchLoans = async () => {
    try {
      setLoading(true);
      const response = await wagesAPI.getLoans({ establishment_id: selectedEstablishment });
      setLoans(response.loansAdvances || []);
    } catch (error) {
      console.error('Error fetching loans:', error);
      setLoans([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async (establishmentId) => {
    try {
      const response = await employeeAPI.getByEstablishment(establishmentId);
      setEmployees(response.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
  };

  const handleEstablishmentChange = (value) => {
    setFormData(prev => ({
      ...prev,
      establishment: value,
      employee: ''
    }));
    setEmployees([]);
    if (value) {
      fetchEmployees(value);
    }
  };

  const calculateMonthlyInstallment = () => {
    const principal = parseFloat(formData.amount) || 0;
    const installmentCount = parseInt(formData.installments) || 1;
    const rate = parseFloat(formData.interestRate) || 0;

    if (rate === 0) {
      return (principal / installmentCount).toFixed(2);
    }

    const monthlyRate = rate / 100 / 12;
    const emi = principal * monthlyRate * Math.pow(1 + monthlyRate, installmentCount) /
                (Math.pow(1 + monthlyRate, installmentCount) - 1);
    return emi.toFixed(2);
  };

  const handleSubmit = async () => {
    if (!formData.establishment || !formData.employee || !formData.loanType || !formData.amount) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.loanType === 'other' && !formData.otherLoanType.trim()) {
      alert('Please specify the loan/advance type');
      return;
    }

    try {
      const loanData = {
        establishmentId: parseInt(formData.establishment),
        selectionType: 'employee',
        employeeId: formData.employee,
        loanType: formData.loanType === 'other' ? formData.otherLoanType.trim() : formData.loanType,
        amount: parseFloat(formData.amount),
        issueDate: formData.issueDate,
        repaymentStartDate: formData.repaymentStartDate,
        installments: parseInt(formData.installments) || 1,
        interestRate: parseFloat(formData.interestRate) || 0,
        reason: formData.reason,
        status: formData.status
      };

      await wagesAPI.addLoan(loanData);
      notifySavedAndReload('Loan/Advance saved successfully!');
      resetForm();
      fetchLoans();
    } catch (error) {
      console.error('Error saving loan/advance:', error);
      alert('Error saving loan/advance: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this loan/advance?')) return;

    try {
      await wagesAPI.deleteLoan(id);
      notifySavedAndReload('Loan/Advance deleted successfully!');
      fetchLoans();
    } catch (error) {
      console.error('Error deleting loan/advance:', error);
      alert('Error deleting loan/advance');
    }
  };

  const resetForm = () => {
    setFormData({
      establishment: lockedEstId || '',
      employee: '',
      loanType: 'salary-advance',
      otherLoanType: '',
      amount: '',
      issueDate: new Date().toISOString().split('T')[0],
      repaymentStartDate: '',
      installments: '',
      interestRate: '0',
      reason: '',
      status: 'active'
    });
    setEmployees([]);
    setShowModal(false);
  };

  const statusStyle = (s) => {
    const map = {
      active:    { bg: '#dcfce7', color: '#15803d', border: '#86efac', label: 'Active' },
      completed: { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd', label: 'Completed' },
      defaulted: { bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5', label: 'Defaulted' },
    };
    const st = map[s] || map.active;
    return {
      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
      padding: '0.25rem 0.7rem', borderRadius: '9999px',
      fontSize: '0.75rem', fontWeight: '600',
      background: st.bg, color: st.color, border: `1px solid ${st.border}`,
    };
  };

  const loanTypeLabel = (t) => {
    const found = loanTypes.find(lt => lt.value === t);
    return found ? found.label : t;
  };

  // ── Shared input style ───────────────────────────────────────────────
  const inputBase = {
    width: '100%', padding: '0.65rem 0.85rem',
    border: '1.5px solid var(--theme-border, #d1d5db)', borderRadius: '0.5rem',
    fontSize: '0.875rem', background: 'var(--theme-surface, #fdf9f3)',
    color: 'var(--theme-text, #111827)', transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  };
  const labelBase = {
    display: 'flex', alignItems: 'center', gap: '0.3rem',
    fontSize: '0.8rem', fontWeight: '600',
    color: 'var(--theme-text, #374151)', marginBottom: '0.4rem',
  };

  return (
    <div style={{ maxWidth: '1100px' }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            width: '42px', height: '42px', borderRadius: '0.65rem',
            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 3px 8px rgba(245,158,11,0.35)',
          }}>
            <HandCoins size={22} color="#fff" />
          </div>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--theme-text, #111827)', margin: 0 }}>
              Loans & Advances
            </h2>
            <p style={{ color: 'var(--theme-textSecondary, #6b7280)', fontSize: '0.8rem', margin: 0 }}>
              Manage employee loans and advances
            </p>
          </div>
        </div>
        {selectedEstablishment && (
          <button
            onClick={() => setShowModal(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.45rem',
              padding: '0.55rem 1.1rem',
              background: 'linear-gradient(135deg, var(--theme-primary, #6366f1), var(--theme-primaryDark, #4f46e5))',
              color: '#fff', border: 'none', borderRadius: '0.5rem',
              fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer',
              boxShadow: '0 3px 8px rgba(99,102,241,0.3)',
              transition: 'transform 0.1s, box-shadow 0.1s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 5px 14px rgba(99,102,241,0.35)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 3px 8px rgba(99,102,241,0.3)'; }}
          >
            <Plus size={16} /> Add Loan
          </button>
        )}
      </div>

      {/* ── Establishment Selector ──────────────────────────────────────── */}
      {!lockedEstId && (
        <EstablishmentCard
          establishments={establishments}
          value={selectedEstablishment}
          onChange={(e) => setSelectedEstablishment(e.target.value)}
        />
      )}

      {/* ── Summary Cards ──────────────────────────────────────────────── */}
      {!loading && loans.length > 0 && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '0.75rem', marginBottom: '1.25rem',
        }}>
          {[
            {
              label: 'Total Disbursed',
              value: `₹${loans.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0).toLocaleString('en-IN')}`,
              color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe',
            },
            {
              label: 'Active Loans',
              value: loans.filter(l => l.status === 'active').length,
              color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0',
            },
            {
              label: 'Total Records',
              value: loans.length,
              color: '#d97706', bg: '#fffbeb', border: '#fde68a',
            },
          ].map(c => (
            <div key={c.label} style={{
              background: c.bg, border: `1px solid ${c.border}`, borderRadius: '0.65rem',
              padding: '0.85rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.15rem',
            }}>
              <span style={{ fontSize: '0.72rem', fontWeight: '600', color: c.color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {c.label}
              </span>
              <span style={{ fontSize: '1.3rem', fontWeight: '800', color: c.color }}>
                {c.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Modal ──────────────────────────────────────────────────────── */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '1rem',
        }}
        onClick={() => resetForm()}>
          <div style={{
            background: 'var(--theme-surface, #fdf9f3)',
            borderRadius: '1rem',
            boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
            width: '100%', maxWidth: '900px', maxHeight: '90vh', overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}>

            {/* Modal Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '1.25rem 1.5rem',
              borderBottom: '1px solid var(--theme-border, #e5e7eb)',
              background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '0.5rem',
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 3px 6px rgba(245,158,11,0.3)',
                }}>
                  <HandCoins size={22} color="white" />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: 'var(--theme-text, #1f2937)', margin: 0 }}>
                    Add Loan / Advance
                  </h3>
                  <p style={{ fontSize: '0.78rem', color: 'var(--theme-textSecondary, #6b7280)', margin: '0.15rem 0 0' }}>
                    Enter loan or advance details for an employee
                  </p>
                </div>
              </div>
              <button onClick={() => resetForm()} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0.4rem', border: 'none', background: 'rgba(0,0,0,0.06)',
                borderRadius: '0.4rem', cursor: 'pointer', color: '#6b7280',
              }}>
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '1.5rem', maxHeight: 'calc(90vh - 180px)', overflowY: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.15rem' }}>

                {/* Organization */}
                {!lockedEstId && (
                  <div>
                    <label style={labelBase}>Organization <span style={{ color: '#dc2626' }}>*</span></label>
                    <select value={formData.establishment} onChange={(e) => handleEstablishmentChange(e.target.value)}
                      style={{ ...inputBase, cursor: 'pointer' }}>
                      <option value="">Select Organization</option>
                      {establishments.map(est => <option key={est.id} value={est.id}>{est.name}</option>)}
                    </select>
                  </div>
                )}

                {/* Employee */}
                {(formData.establishment || lockedEstId) && (
                  <div>
                    <label style={labelBase}>Employee <span style={{ color: '#dc2626' }}>*</span></label>
                    <select value={formData.employee}
                      onChange={(e) => setFormData(prev => ({ ...prev, employee: e.target.value }))}
                      style={{ ...inputBase, cursor: 'pointer' }}>
                      <option value="">Select Employee</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.employee_id}>
                          {emp.first_name} {emp.last_name} ({emp.employee_id}) - {emp.department || 'N/A'}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Loan Details */}
                {formData.employee && (
                  <>
                    <div style={{ height: '1px', background: 'var(--theme-border, #e5e7eb)' }} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>

                      <div>
                        <label style={labelBase}>Loan Type <span style={{ color: '#dc2626' }}>*</span></label>
                        <select value={formData.loanType}
                          onChange={(e) => setFormData(prev => ({ ...prev, loanType: e.target.value }))}
                          style={{ ...inputBase, cursor: 'pointer' }}>
                          {loanTypes.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                        </select>
                      </div>

                      {formData.loanType === 'other' && (
                        <div>
                          <label style={labelBase}>Specify Type <span style={{ color: '#dc2626' }}>*</span></label>
                          <input type="text" value={formData.otherLoanType}
                            onChange={(e) => setFormData(prev => ({ ...prev, otherLoanType: e.target.value }))}
                            placeholder="Enter type name" style={inputBase} />
                        </div>
                      )}

                      <div>
                        <label style={labelBase}><IndianRupee size={13} /> Amount <span style={{ color: '#dc2626' }}>*</span></label>
                        <input type="number" value={formData.amount}
                          onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                          placeholder="0.00" min="0" step="0.01" style={inputBase} />
                      </div>

                      <div>
                        <label style={labelBase}><Calendar size={13} /> Issue Date</label>
                        <DateInput value={formData.issueDate}
                          onChange={(e) => setFormData(prev => ({ ...prev, issueDate: e.target.value }))}
                          style={inputBase} />
                      </div>

                      <div>
                        <label style={labelBase}><Calendar size={13} /> Repayment Start</label>
                        <DateInput value={formData.repaymentStartDate}
                          onChange={(e) => setFormData(prev => ({ ...prev, repaymentStartDate: e.target.value }))}
                          style={inputBase} />
                      </div>

                      <div>
                        <label style={labelBase}><Hash size={13} /> Installments (Months)</label>
                        <input type="number" value={formData.installments}
                          onChange={(e) => setFormData(prev => ({ ...prev, installments: e.target.value }))}
                          placeholder="e.g. 12" min="1" style={inputBase} />
                      </div>

                      <div>
                        <label style={labelBase}><Percent size={13} /> Interest Rate (% p.a.)</label>
                        <input type="number" value={formData.interestRate}
                          onChange={(e) => setFormData(prev => ({ ...prev, interestRate: e.target.value }))}
                          placeholder="0" min="0" step="0.1" style={inputBase} />
                      </div>

                      {/* EMI Preview */}
                      {formData.amount && formData.installments && (
                        <div style={{
                          gridColumn: '1 / -1',
                          padding: '0.75rem 1rem', borderRadius: '0.5rem',
                          background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
                          border: '1px solid #6ee7b7',
                          display: 'flex', alignItems: 'center', gap: '0.5rem',
                        }}>
                          <Calculator size={16} style={{ color: '#059669' }} />
                          <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#047857' }}>
                            Monthly EMI: ₹{calculateMonthlyInstallment()}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#059669' }}>
                            ({formData.installments} months)
                          </span>
                        </div>
                      )}

                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={labelBase}><FileText size={13} /> Reason / Purpose</label>
                        <textarea value={formData.reason}
                          onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                          rows={3} placeholder="Enter reason for loan or advance"
                          style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }} />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: '0.65rem',
              padding: '1rem 1.5rem',
              borderTop: '1px solid var(--theme-border, #e5e7eb)',
              background: 'var(--theme-surfaceHover, #f0ebe0)',
            }}>
              <button onClick={resetForm} style={{
                padding: '0.55rem 1.1rem', border: '1px solid var(--theme-border, #d1d5db)',
                background: 'var(--theme-surface, #fdf9f3)', borderRadius: '0.5rem',
                fontSize: '0.85rem', fontWeight: '600', color: 'var(--theme-textSecondary, #374151)',
                cursor: 'pointer',
              }}>
                Cancel
              </button>
              <button onClick={handleSubmit} disabled={!formData.employee} style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                padding: '0.55rem 1.35rem',
                background: !formData.employee
                  ? '#9ca3af'
                  : 'linear-gradient(135deg, var(--theme-primary, #6366f1), var(--theme-primaryDark, #4f46e5))',
                color: '#fff', border: 'none', borderRadius: '0.5rem',
                fontSize: '0.85rem', fontWeight: '600',
                cursor: !formData.employee ? 'not-allowed' : 'pointer',
                boxShadow: !formData.employee ? 'none' : '0 3px 8px rgba(99,102,241,0.3)',
              }}>
                <Save size={16} /> Save Loan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Table ──────────────────────────────────────────────────────── */}
      {selectedEstablishment && (
        <div style={{
          background: 'var(--theme-surface, #fdf9f3)',
          borderRadius: '0.75rem',
          border: '1px solid var(--theme-border, #e5e7eb)',
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}>
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center' }}>
              <div style={{
                width: '36px', height: '36px', margin: '0 auto 0.75rem',
                border: '3px solid var(--theme-border, #e5e7eb)',
                borderTopColor: 'var(--theme-primary, #6366f1)',
                borderRadius: '50%', animation: 'spin 0.8s linear infinite',
              }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <p style={{ color: 'var(--theme-textSecondary)', fontSize: '0.85rem', margin: 0 }}>Loading loans...</p>
            </div>
          ) : loans.length === 0 ? (
            <div style={{ padding: '3.5rem 2rem', textAlign: 'center' }}>
              <div style={{
                width: '64px', height: '64px', margin: '0 auto 1rem', borderRadius: '50%',
                background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <HandCoins size={28} style={{ color: '#d97706' }} />
              </div>
              <p style={{ color: 'var(--theme-text, #374151)', fontSize: '0.95rem', fontWeight: '600', margin: '0 0 0.25rem' }}>
                No loans or advances yet
              </p>
              <p style={{ color: 'var(--theme-textSecondary, #9ca3af)', fontSize: '0.82rem', margin: 0 }}>
                Click "Add Loan" to create the first record.
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{
                    background: 'linear-gradient(135deg, var(--theme-surfaceHover, #f0ebe0), var(--theme-surface, #fdf9f3))',
                    borderBottom: '2px solid var(--theme-border, #e5e7eb)',
                  }}>
                    {['Employee', 'Type', 'Amount', 'Installments', 'Issue Date', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{
                        textAlign: h === 'Amount' ? 'right' : h === 'Installments' || h === 'Actions' ? 'center' : 'left',
                        padding: '0.75rem 1rem', fontSize: '0.72rem', fontWeight: '700',
                        color: 'var(--theme-textSecondary, #6b7280)',
                        textTransform: 'uppercase', letterSpacing: '0.05em',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loans.map((loan, i) => (
                    <tr key={loan.id} style={{
                      borderBottom: '1px solid var(--theme-border, #f0f0f0)',
                      background: i % 2 === 0 ? 'var(--theme-surface, #fdf9f3)' : 'var(--theme-surfaceHover, #f0ebe0)',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--theme-accent, #eef2ff)'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'var(--theme-surface, #fdf9f3)' : 'var(--theme-surfaceHover, #f0ebe0)'}
                    >
                      <td style={{ padding: '0.8rem 1rem' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--theme-text, #111827)' }}>
                          {loan.employee_name || loan.employee_id}
                        </span>
                      </td>
                      <td style={{ padding: '0.8rem 1rem' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                          padding: '0.2rem 0.6rem', borderRadius: '0.35rem',
                          background: '#f5f3ff', color: '#6d28d9', border: '1px solid #ddd6fe',
                          fontSize: '0.78rem', fontWeight: '600',
                        }}>
                          {loanTypeLabel(loan.loan_type)}
                        </span>
                      </td>
                      <td style={{
                        padding: '0.8rem 1rem', textAlign: 'right',
                        fontSize: '0.9rem', fontWeight: '700', color: '#b45309',
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                        ₹{parseFloat(loan.amount).toLocaleString('en-IN')}
                      </td>
                      <td style={{
                        padding: '0.8rem 1rem', textAlign: 'center',
                        fontSize: '0.85rem', fontWeight: '600', color: 'var(--theme-text, #374151)',
                      }}>
                        {loan.installments || '-'}
                      </td>
                      <td style={{
                        padding: '0.8rem 1rem', fontSize: '0.84rem',
                        color: 'var(--theme-textSecondary, #6b7280)',
                      }}>
                        {loan.issue_date || '-'}
                      </td>
                      <td style={{ padding: '0.8rem 1rem' }}>
                        <span style={statusStyle(loan.status)}>
                          <span style={{
                            width: '6px', height: '6px', borderRadius: '50%',
                            background: 'currentColor', display: 'inline-block',
                          }} />
                          {loan.status ? loan.status.charAt(0).toUpperCase() + loan.status.slice(1) : 'Active'}
                        </span>
                      </td>
                      <td style={{ padding: '0.8rem 1rem', textAlign: 'center' }}>
                        <button onClick={() => handleDelete(loan.id)} title="Delete" style={{
                          padding: '0.35rem', background: '#fef2f2', border: '1px solid #fecaca',
                          borderRadius: '0.4rem', cursor: 'pointer', color: '#dc2626',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LoansAdvances;
